import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ReferralStatus } from '@shs-digi-care-monorepo/interfaces';
import type { Referral } from '../model/referral.model';

@Component({
    selector: 'sh-referral-overview',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    host: { class: 'block h-full' },
    template: `
        <section class="h-full">
            @if (referrals().length > 0) {
                <ul class="list-none flex flex-wrap gap-2 w-full m-0 p-0 mb-4">
                    @for (referral of openReferrals(); track referral.id) {
                        <li
                            class="flex bg-shui-base-3 px-4 py-[9.5px] rounded-lg justify-between m-0 box-border border border-transparent hover:bg-shui-base-4 hover:border-shui-base-5 cursor-pointer w-[469px] h-[81px]"
                            (click)="referralClicked.emit(referral)"
                        >
                            <div class="flex flex-col">
                                <sh-badge size="medium" [label]="'Active'" variant="squared"></sh-badge>
                                <p class="text-sm/6 m-0 text-[#457070] dark:text-[#87D2D2] flex items-center">
                                    {{ referral.name }}
                                </p>
                                <p class="text-sm m-0 truncate max-w-[270px] text-shui-text-strong flex items-center">
                                    {{ referral.description }}
                                </p>
                            </div>
                            <div class="self-center flex items-center gap-2">
                                <sh-icon [icon]="referral.referredOutTeam ? 'group' : 'user'" size="s"></sh-icon>
                                <sh-tooltip
                                    [label]="referral.referredOutEmail || referral.referredOutTeam || '-'"
                                    [target]="'referral-assignee-' + referral.id"
                                    placement="top"
                                    variation="short"
                                ></sh-tooltip>
                            </div>
                        </li>
                    }
                </ul>
                @if (openReferrals().length > 0 && closedReferrals().length > 0) {
                    <sh-divider spacing="m"></sh-divider>
                }
                @if (closedReferrals().length > 0) {
                    <ul class="list-none flex flex-wrap gap-2 w-full m-0 p-0 mb-4">
                        @for (referral of closedReferrals(); track referral.id) {
                            <li
                                class="flex bg-shui-base-3 px-4 py-[9.5px] rounded-lg justify-between m-0 box-border border border-transparent hover:bg-shui-base-4 hover:border-shui-base-5 cursor-pointer opacity-50 w-[469px] h-[81px]"
                                (click)="referralClicked.emit(referral)"
                            >
                                <div class="flex flex-col">
                                    <sh-badge size="medium" [label]="'Completed'" variant="squared"></sh-badge>
                                    <p class="text-sm/6 m-0 text-[#457070] dark:text-[#87D2D2] flex items-center">
                                        {{ referral.name }}
                                    </p>
                                    <p
                                        class="text-sm m-0 truncate max-w-[270px] text-shui-text-strong flex items-center"
                                    >
                                        {{ referral.description }}
                                    </p>
                                </div>
                                <div class="self-center flex items-center gap-2">
                                    <sh-icon [icon]="referral.referredOutTeam ? 'group' : 'user'" size="s"></sh-icon>
                                    <sh-tooltip
                                        [label]="referral.referredOutEmail || referral.referredOutTeam || '-'"
                                        [target]="'referral-assignee-closed-' + referral.id"
                                        placement="top"
                                        variation="short"
                                    ></sh-tooltip>
                                </div>
                            </li>
                        }
                    </ul>
                }
            } @else {
                <div class="flex flex-col w-full flex-1 min-h-0 items-center justify-center gap-4 h-full">
                    <sh-icon icon="wellbeing-average" size="l" class="text-shui-text-subtle opacity-20"></sh-icon>
                    <p class="text-sm/6 m-0 text-shui-text-subtle">
                        {{ 'request.requestDetail.referral-overview.no-referrals-found' | translate }}
                    </p>
                </div>
            }
        </section>
    `
})
export class ReferralOverviewComponent {
    public readonly referrals = input<Referral[]>([]);
    public readonly referralClicked = output<Referral>();

    protected readonly openReferrals = computed(() => this.referrals().filter((r) => r.status === ReferralStatus.OPEN));

    protected readonly closedReferrals = computed(() =>
        this.referrals().filter((r) => r.status === ReferralStatus.CLOSED)
    );
}
